import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRightIcon, MicIcon, RotateCcwIcon, XIcon } from 'lucide-react';
import { CardRenderer } from './CardRenderer';
import { TaskRunCard } from './TaskRunCard';
import { VoiceListening } from './VoiceListening';
import { scenarios } from '../../data/scenarios';
import { LEXCE_AVATAR } from '../../data/homeScreen';
import type { LexceStatus } from '../../hooks/useLexceConversation';
import type { LexceTurn, ScenarioId } from '../../types/lexce';
interface LexceAssistantProps {
  open: boolean;
  turns: LexceTurn[];
  status: LexceStatus;
  activeId: ScenarioId;
  onClose: () => void;
  onStart: (id: Exclude<ScenarioId, 'none'>) => void;
  onReset: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
export function LexceAssistant({
  open,
  turns,
  status,
  activeId,
  onClose,
  onStart,
  onReset
}: LexceAssistantProps) {
  const endRef = useRef<HTMLDivElement>(null);
  const pendingUtterance = scenarios.find(s => s.id === activeId)?.utterance ?? '';
  useEffect(() => {
    endRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'end'
    });
  }, [turns.length, status]);
  const cycleMic = () => {
    const index = scenarios.findIndex(s => s.id === activeId);
    const next = scenarios[(index + 1) % scenarios.length];
    onStart(next.id);
  };
  return <AnimatePresence data-id="element-55" data-mp="zhq2re3p4ilm">
      {open && <div className="absolute inset-0 z-30 flex flex-col justify-end" data-id="element-56" data-mp="ukpv4yr777se">
          <motion.button type="button" aria-label="Close Lexce" onClick={onClose} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: EASE
      }} className="absolute inset-0 bg-vin-ink/45" data-id="element-57" data-mp="plmq3eb7x1kf" />

          <motion.section role="dialog" aria-label="Hey Lexce assistant" initial={{
        y: '100%'
      }} animate={{
        y: 0
      }} exit={{
        y: '100%'
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} className="relative flex h-[88%] flex-col rounded-t-[28px] bg-white shadow-sheet" data-id="element-58" data-mp="2fxait9o3zon7">
            <header className="flex items-center gap-3 border-b border-vin-line px-4 py-3" data-id="element-59" data-mp="2l368kwg1w2ga">
              <img src={LEXCE_AVATAR} alt="" className="h-9 w-9 rounded-full object-cover" data-id="element-60" data-mp="2q1hgv2qp64we" />
              <div className="min-w-0 flex-1" data-id="element-61" data-mp="3drbqzcjmtfcy">
                <h2 className="text-[15px] font-bold leading-5 text-vin-ink" data-id="element-62" data-mp="3dsmwbgsq6v66">Lexce</h2>
                <p className="text-[11px] text-vin-muted" data-id="element-63" data-mp="3hrvrmq3ez13u">
                  {status === 'listening' ? 'Listening…' : status === 'thinking' ? 'Thinking…' : status === 'working' ? 'Acting on your request…' : 'Your VinUni assistant'}
                </p>
              </div>
              {turns.length > 0 && <button type="button" onClick={onReset} className="rounded-full p-2 text-vin-muted transition-colors duration-150 ease-out hover:bg-vin-mist hover:text-vin-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vin-tertiary" aria-label="Start a new conversation" data-id="element-64" data-mp="1xaz8bm77fbbl">
                  <RotateCcwIcon className="h-4 w-4" aria-hidden="true" data-id="element-65" data-mp="165adey7rpqv1" />
                </button>}
              <button type="button" onClick={onClose} className="rounded-full p-2 text-vin-muted transition-colors duration-150 ease-out hover:bg-vin-mist hover:text-vin-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vin-tertiary" aria-label="Close assistant" data-id="element-66" data-mp="124i1tybfb9vl">
                <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-67" data-mp="igis8qsli8rr" />
              </button>
            </header>

            <div className="lexce-scroll flex-1 overflow-y-auto px-4 py-4" data-id="element-68" data-mp="w3hhd5uzr398">
              {status === 'listening' && <VoiceListening utterance={pendingUtterance} data-id="element-69" data-mp="112uecjfktqpj" />}

              {status === 'idle' && turns.length === 0 && <div className="pb-2" data-id="element-70" data-mp="15zb41i2fcd4q">
                  <img src={LEXCE_AVATAR} alt="" className="h-16 w-16 rounded-full object-cover" data-id="element-71" data-mp="1arm1me9ozmu4" />
                  <h3 className="mt-3 text-[22px] font-bold leading-7 text-vin-ink" data-id="element-72" data-mp="1et7lijkn91dt">
                    Hi Minh Anh — what do you need?
                  </h3>
                  <p className="mt-1 text-[13px] leading-5 text-vin-muted" data-id="element-73" data-mp="1jqpo7s82ikdd">
                    Ask a question, or just say “Hey Lexce”. I can also finish the task for you afterwards.
                  </p>
                  <ul className="mt-5 space-y-2" data-id="element-74" data-mp="1tpovb6d6q7y9">
                    {scenarios.map(scenario => <li key={scenario.id} data-id="element-75" data-mp="3qktdnwymlkd4">
                        <button type="button" onClick={() => onStart(scenario.id)} className="flex w-full items-center gap-3 rounded-2xl border border-vin-line px-3.5 py-3 text-left transition-colors duration-150 ease-out hover:border-vin-tertiary hover:bg-vin-mist focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vin-tertiary" data-id="element-76" data-mp="3vhbfanynt2l8">
                          <span className="min-w-0 flex-1" data-id="element-77" data-mp="2vlb49w7ywmhu">
                            <span className="block text-[13px] font-semibold text-vin-ink" data-id="element-78" data-mp="2vkrvb50ap8fy">{scenario.label}</span>
                            <span className="mt-0.5 block text-[12px] leading-4 text-vin-muted" data-id="element-79" data-mp="2rhvcbd6ufabh">
                              {scenario.summary}
                            </span>
                          </span>
                          <ChevronRightIcon className="h-4 w-4 shrink-0 text-vin-tertiary" aria-hidden="true" data-id="element-80" data-mp="1b5pwxffcxars" />
                        </button>
                      </li>)}
                  </ul>
                </div>}

              <div className="space-y-3" data-id="element-81" data-mp="17p3ivx2qdf4o">
                {turns.map(turn => <motion.div key={turn.id} initial={{
              opacity: 0,
              y: 10,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              duration: 0.24,
              ease: EASE
            }} data-id="element-82" data-mp="1cltzqmmd0er5">
                    {turn.kind === 'user' && <div className="flex justify-end" data-id="element-83" data-mp="ovrccjte3wyv">
                        <p className="max-w-[85%] rounded-2xl rounded-br-md bg-vin-primary px-3.5 py-2.5 text-[13px] leading-5 text-white" data-id="element-84" data-mp="tokgp6ky53af">
                          {turn.text}
                        </p>
                      </div>}

                    {turn.kind === 'say' && <div className="flex gap-2.5" data-id="element-85" data-mp="5xwvxkpxmj6g">
                        <img src={LEXCE_AVATAR} alt="" className="mt-0.5 h-7 w-7 shrink-0 rounded-full object-cover" data-id="element-86" data-mp="60jbo7mz84zr" />
                        <div className="min-w-0" data-id="element-87" data-mp="3nh5fs2vdld72">
                          <p className="rounded-2xl rounded-bl-md bg-vin-mist px-3.5 py-2.5 text-[13px] leading-5 text-vin-ink" data-id="element-88" data-mp="3nhx1mlz67wk5">
                            {turn.text}
                          </p>
                          {turn.highlights && <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-89" data-mp="3sg8bgwpgu72d">
                              {turn.highlights.map(highlight => <span key={highlight} className="rounded-md bg-white px-2 py-0.5 text-[11px] font-semibold text-vin-primary ring-1 ring-vin-line" data-id="element-90" data-mp="a9aqwd6zyv59">
                                  {highlight}
                                </span>)}
                            </div>}
                        </div>
                      </div>}

                    {turn.kind === 'task' && <TaskRunCard task={turn.task} data-id="element-91" data-mp="oxorehgxy7in" />}
                    {turn.kind === 'card' && <CardRenderer card={turn.card} data-id="element-92" data-mp="1ve7oh3kjhaes" />}

                    {turn.kind === 'chips' && <div className="flex flex-wrap gap-2 pt-1" data-id="element-93" data-mp="20fn8qtew7cw4">
                        {turn.chips.map(chip => <button key={chip} type="button" className="rounded-full border border-vin-line px-3 py-1.5 text-[12px] font-medium text-vin-primary transition-colors duration-150 ease-out hover:bg-vin-mist focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vin-tertiary" data-id="element-94" data-mp="29e8mmb9f63qk">
                            {chip}
                          </button>)}
                      </div>}
                  </motion.div>)}

                {(status === 'thinking' || status === 'working') && turns[turns.length - 1]?.kind !== 'task' && <div className="flex gap-2.5" data-id="element-95" data-mp="qerjn63gm41z">
                    <img src={LEXCE_AVATAR} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" data-id="element-96" data-mp="lfy6jes34f2c" />
                    <div className="flex items-center gap-1 rounded-2xl rounded-bl-md bg-vin-mist px-3.5 py-3" data-id="element-97" data-mp="he36edlw47q5">
                      {[0, 1, 2].map(dot => <motion.span key={dot} className="h-1.5 w-1.5 rounded-full bg-vin-tertiary" animate={{
                  opacity: [0.3, 1, 0.3]
                }} transition={{
                  duration: 0.9,
                  repeat: Infinity,
                  ease: 'easeInOut',
                  delay: dot * 0.15
                }} data-id="element-98" data-mp="ci47vx6ul9c0" />)}
                    </div>
                  </div>}
              </div>
              <div ref={endRef} data-id="element-99" data-mp="3kv1t6vgpua8b" />
            </div>

            <div className="flex items-center gap-2 border-t border-vin-line px-4 pb-5 pt-3" data-id="element-100" data-mp="3fwrxmjr2x7m6">
              <div className="flex-1 rounded-full bg-vin-mist px-4 py-2.5 text-[13px] text-vin-muted" data-id="element-101" data-mp="15s27br4lpa8c">
                Message Lexce
              </div>
              <button type="button" onClick={cycleMic} aria-label="Talk to Lexce" className={`relative flex h-11 w-11 items-center justify-center rounded-full text-white transition-colors duration-150 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-vin-tertiary ${status === 'listening' ? 'bg-vin-secondary' : 'bg-vin-primary hover:bg-vin-deep'}`} data-id="element-102" data-mp="11pyx15to4bon">
                {status === 'listening' && <motion.span className="absolute inset-0 rounded-full bg-vin-secondary/40" animate={{
              scale: [1, 1.45],
              opacity: [0.6, 0]
            }} transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeOut'
            }} data-id="element-103" data-mp="rs180g3ziauz" />}
                <MicIcon className="relative h-5 w-5" aria-hidden="true" data-id="element-104" data-mp="3l41e0unq3tv3" />
              </button>
            </div>
          </motion.section>
        </div>}
    </AnimatePresence>;
}